import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, UserMinus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Row {
  user_id: string;
  name: string;
  points: number;
  exact: number;
  outcome: number;
  count: number;
}

export const LeaderboardCard = ({
  rows,
  currentUserId,
  onEditNickname,
  editingNickname,
  isAdmin,
  ownerId,
  onRemovePlayer,
  onPlayerClick,
}: {
  rows: Row[];
  currentUserId: string;
  onEditNickname?: () => void;
  editingNickname?: boolean;
  isAdmin?: boolean;
  ownerId?: string;
  onRemovePlayer?: (userId: string) => Promise<void> | void;
  onPlayerClick?: (userId: string) => void;
}) => {
  const { t } = useTranslation();
  const [toRemove, setToRemove] = useState<Row | null>(null);
  const [removing, setRemoving] = useState(false);

  const confirmRemove = async () => {
    if (!toRemove || !onRemovePlayer) return;
    setRemoving(true);
    try {
      await onRemovePlayer(toRemove.user_id);
    } finally {
      setRemoving(false);
      setToRemove(null);
    }
  };

  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const isMe = r.user_id === currentUserId;
        const canRemove =
          isAdmin && onRemovePlayer && r.user_id !== ownerId && r.user_id !== currentUserId;
        return (
          <div
            key={r.user_id}
            onClick={() => onPlayerClick?.(r.user_id)}
            role={onPlayerClick ? "button" : undefined}
            tabIndex={onPlayerClick ? 0 : undefined}
            onKeyDown={(e) => {
              if (onPlayerClick && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onPlayerClick(r.user_id);
              }
            }}
            className={`relative flex items-center gap-3 rounded-2xl border bg-card px-3 py-3 shadow-card transition-shadow hover:shadow-elevated ${
              onPlayerClick ? "cursor-pointer" : ""
            } ${
              isMe ? "border-transparent ring-2 ring-primary/50 shadow-gold" : "border-border"
            }`}
          >
            {isMe && (
              <span className="absolute inset-y-0 left-0 w-1 rounded-l-2xl bg-gradient-hero" />
            )}
            <div
              className={`grid h-10 w-10 shrink-0 place-items-center rounded-full text-sm font-bold ${
                i === 0
                  ? "bg-gradient-gold text-primary-foreground shadow-gold"
                  : i === 1
                  ? "bg-gradient-pitch text-primary-foreground"
                  : i === 2
                  ? "bg-secondary text-secondary-foreground ring-1 ring-primary/20"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {i === 0 ? "🏆" : i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold truncate max-w-full">{r.name}</div>
                {isMe && onEditNickname && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onEditNickname(); }}
                    aria-label={t("league.changeNickname")}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${
                      editingNickname
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                  >
                    <Pencil className="h-3 w-3" />
                    <span className="hidden sm:inline">{t("league.changeNickname")}</span>
                  </button>
                )}
                {canRemove && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setToRemove(r); }}
                    aria-label={t("league.removePlayer")}
                    title={t("league.removePlayer")}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
                  >
                    <UserMinus className="h-3 w-3" />
                    <span className="hidden sm:inline">{t("league.removePlayer")}</span>
                  </button>
                )}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
                <span>{t("league.exact")}: <b className="text-foreground">{r.exact}</b></span>
                <span>{t("league.outcome")}: <b className="text-foreground">{r.outcome}</b></span>
                <span>{t("league.tipsShort")}: {r.count}</span>
              </div>
            </div>
            <div className="shrink-0 text-right pl-2 min-w-[48px]">
              <div className="text-2xl font-bold tabular-nums leading-none text-primary">
                {r.points}
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{t("league.pointsShort")}</div>
            </div>
          </div>
        );
      })}

      <AlertDialog open={!!toRemove} onOpenChange={(o) => !o && setToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("league.removePlayer")}</AlertDialogTitle>
            <AlertDialogDescription>
              {toRemove && t("league.confirmRemovePlayer", { name: toRemove.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmRemove(); }}
              disabled={removing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("league.removePlayer")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
